/**
 * Page 6 — Section 03: Top Findings & Recommendations.
 *
 * Selection logic (curated, not a data dump):
 *   1. Sort findings by Risk (High/Critical → Moderate → Low) and then
 *      Priority (Immediate → Quick Win → Strategic).
 *   2. Pick all High/Critical+Immediate first, then remaining High/Critical,
 *      then top up with Moderate (Immediate, then Quick Win first).
 *   3. Hard-cap at 10 rows.
 *
 * Data:
 *   • Findings come from `reviewer_get_findings` (the Findings tab source);
 *     the RPC already returns `csv_domain` and `nist_function`.
 *   • Cyber resilience band is resolved upstream (override or NIST average).
 *
 * Layout:
 *   • Eyebrow + Title + intro paragraph
 *   • 4 summary metric cards (Total / High+Critical / Top shown / Resilience)
 *   • Top-10 findings table (jspdf-autotable) with color badges and
 *     consistent styling with Page 5.
 */
import type jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PAGE_MARGIN, REPORT_THEME, type RGB } from "./theme";
import { drawRunningHeader, drawPageFooter, drawSectionTitle } from "./pageChrome";
import type { MaturityBand } from "@/reports/telecom/cyberResilienceSummary";
import { drawCyberResilienceCard } from "./cyberResilienceCard";

export interface TopFindingRow {
  id?: string;
  display_id?: string | null;
  display_seq?: number | null;
  question_id?: number | null;
  control_code?: string | null;
  finding_title?: string | null;
  recommended_action?: string | null;
  priority_level?: string | null;
  risk_level?: string | null;
  csv_domain?: string | null;
  nist_function?: string | null;
  due_date?: string | null;
}

export interface TopFindingsContext {
  findings: TopFindingRow[];
  overallBand: MaturityBand | null;
  clientOrgName: string;
  methodology?: string | null;
  frameworks?: string[] | null;
  pageNumber?: number;
}

// ── Sort weights ──────────────────────────────────────────────────────
const RISK_WEIGHT: Record<string, number> = {
  Critical: 0,
  High: 1,
  HIGH: 1,
  Moderate: 2,
  MODERATE: 2,
  Medium: 2,
  Low: 3,
  LOW: 3,
};
const PRIORITY_WEIGHT: Record<string, number> = {
  Immediate: 0,
  "Quick Win": 1,
  Strategic: 2,
};

function isHighOrCritical(r: TopFindingRow): boolean {
  const v = (r.risk_level ?? "").toLowerCase();
  return v === "high" || v === "critical";
}
function isModerate(r: TopFindingRow): boolean {
  const v = (r.risk_level ?? "").toLowerCase();
  return v === "moderate" || v === "medium";
}
function isImmediate(r: TopFindingRow): boolean {
  return (r.priority_level ?? "").toLowerCase() === "immediate";
}
function isQuickWin(r: TopFindingRow): boolean {
  return (r.priority_level ?? "").toLowerCase() === "quick win";
}

/**
 * Sort by Due Date ASC (nulls last) → Priority (Immediate, Quick Win,
 * Strategic, Other) → display_seq → control_code. Matches the CAP and
 * Remediation Roadmap ordering so finding #1 in Executive Summary,
 * Roadmap, and CAP are the same row.
 */
function dueTs(v?: string | null): number {
  if (!v) return Number.POSITIVE_INFINITY;
  const t = new Date(v).getTime();
  return isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

function compare(a: TopFindingRow, b: TopFindingRow): number {
  const ta = dueTs(a.due_date);
  const tb = dueTs(b.due_date);
  if (ta !== tb) return ta - tb;
  const pa = PRIORITY_WEIGHT[a.priority_level ?? ""] ?? 9;
  const pb = PRIORITY_WEIGHT[b.priority_level ?? ""] ?? 9;
  if (pa !== pb) return pa - pb;
  const sa = a.display_seq ?? Number.POSITIVE_INFINITY;
  const sb = b.display_seq ?? Number.POSITIVE_INFINITY;
  if (sa !== sb) return sa - sb;
  return (a.control_code ?? "").localeCompare(b.control_code ?? "");
}

/** Sorted Top-N selection (due date → priority → display_seq). */
export function selectTopFindings(all: TopFindingRow[], n = 10): TopFindingRow[] {
  return [...all].sort(compare).slice(0, n);
}

// ── Color tokens for badges (consistent with Page 5 system) ──────────
const RISK_BADGE: Record<string, { fill: RGB; text: RGB }> = {
  Critical: { fill: [153, 27, 27], text: [255, 255, 255] }, // red-800
  High:     { fill: [220, 38, 38], text: [255, 255, 255] }, // red-600
  Moderate: { fill: [245, 158, 11], text: [255, 255, 255] }, // amber-500
  Medium:   { fill: [245, 158, 11], text: [255, 255, 255] },
  Low:      { fill: [148, 163, 184], text: [255, 255, 255] }, // slate-400 (gray)
};
const PRIORITY_BADGE: Record<string, { fill: RGB; text: RGB }> = {
  Immediate:    { fill: [220, 38, 38], text: [255, 255, 255] },
  "Quick Win":  { fill: [201, 162, 39], text: [255, 255, 255] }, // gold
  Strategic:    { fill: [37, 99, 235], text: [255, 255, 255] }, // blue-600
};

function normalizeRisk(v?: string | null): string {
  if (!v) return "—";
  const s = String(v).trim();
  const lower = s.toLowerCase();
  if (lower === "high") return "High";
  if (lower === "critical") return "Critical";
  if (lower === "moderate" || lower === "medium") return "Moderate";
  if (lower === "low") return "Low";
  return s;
}
function normalizePriority(v?: string | null): string {
  if (!v) return "—";
  const s = String(v).trim();
  const lower = s.toLowerCase();
  if (lower === "immediate") return "Immediate";
  if (lower === "quick win") return "Quick Win";
  if (lower === "strategic") return "Strategic";
  return s;
}

function truncate(s: string, n: number): string {
  if (!s) return "";
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + "…";
}

// ── Renderer ─────────────────────────────────────────────────────────
export function renderTopFindings(doc: jsPDF, ctx: TopFindingsContext): void {
  doc.addPage();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  drawRunningHeader(doc, {
    clientName: ctx.clientOrgName,
    frameworks: ctx.frameworks ?? null,
    methodology: ctx.methodology ?? null,
  });

  let y = drawSectionTitle(doc, {
    sectionNumber: "04",
    title: "Top Findings & Recommendations",
  });

  doc.setTextColor(...REPORT_THEME.textMuted.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  const subtitle =
    "Top cybersecurity risks prioritized by residual risk and remediation urgency.";
  doc.text(subtitle, PAGE_MARGIN, y);
  y += 18;

  // ── Summary metric cards ──
  const all = ctx.findings ?? [];
  const TOP_N = 5;
  const top = selectTopFindings(all, TOP_N);
  const highCount = all.filter(
    (r) => (r.risk_level ?? "").toLowerCase() === "high" ||
           (r.risk_level ?? "").toLowerCase() === "critical",
  ).length;

  const metrics = [
    { label: "Total Findings", value: String(all.length) },
    { label: "High / Critical", value: String(highCount) },
    { label: "Top Findings Shown", value: String(top.length) },
  ];

  const cardGap = 12;
  const cardsW = pageW - PAGE_MARGIN * 2;
  const cardW = (cardsW - cardGap * 3) / 4;
  const cardH = 56;

  metrics.forEach((m, i) => {
    const cx = PAGE_MARGIN + i * (cardW + cardGap);
    doc.setFillColor(...REPORT_THEME.surfaceAlt.rgb);
    doc.setDrawColor(...REPORT_THEME.border.rgb);
    doc.setLineWidth(0.6);
    doc.roundedRect(cx, y, cardW, cardH, 6, 6, "FD");

    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(m.label.toUpperCase(), cx + 12, y + 18);

    doc.setTextColor(...REPORT_THEME.navy.rgb);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(m.value, cx + 12, y + 42);
  });

  // Cyber Resilience card — shared tinted card (matches Section 08)
  const resilienceX = PAGE_MARGIN + 3 * (cardW + cardGap);
  drawCyberResilienceCard(
    doc,
    resilienceX,
    y,
    cardW,
    cardH,
    ctx.overallBand ?? "Unrated",
  );


  y += cardH + 18;

  // ── Intro paragraph ──
  doc.setTextColor(...REPORT_THEME.textPrimary.rgb);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  const intro =
    "The following findings represent the most material cybersecurity risks identified in this " +
    "assessment, prioritized based on risk severity and remediation urgency. Each finding is mapped " +
    "to its CSV Cybersecurity Domain and the corresponding NIST CSF 2.0 control reference. The full " +
    "findings list is available in the Appendix.";
  const introLines = doc.splitTextToSize(intro, pageW - PAGE_MARGIN * 2);
  doc.text(introLines, PAGE_MARGIN, y, { lineHeightFactor: 1.4 } as any);
  y += introLines.length * 12 + 10;

  // ── Top-N table (variable-height rows, multi-page safe) ──
  // Recommendation text must NEVER be truncated for an executive report.
  // We let autoTable size rows from content; rows that don't fit are pushed
  // to the next page (rowPageBreak: "avoid") and we draw the running header
  // + section title (cont'd) on each continuation page.
  const FOOTER_RESERVE = 56;
  const TOP_MARGIN_CONT = 120; // top margin on continuation pages (under cont'd title)
  const tableTop = y;

  const body = top.map((r, i) => {
    const csvDomain = (r.csv_domain ?? "").trim() || "—";
    const nistFn = (r.nist_function ?? "").trim();
    const controlCode = (r.control_code ?? "").trim();
    const nistRef =
      controlCode && /^[A-Z]{2}\.[A-Z]{2}-\d+/i.test(controlCode)
        ? controlCode.toUpperCase()
        : nistFn;
    const action = (r.recommended_action ?? "—").trim() || "—";
    const idLabel = (r.display_id && String(r.display_id).trim()) || String(i + 1);
    return [
      idLabel,
      { title: (r.finding_title ?? "Untitled finding").trim() } as any,
      { csv: csvDomain, nist: nistRef } as any,
      normalizeRisk(r.risk_level),
      normalizePriority(r.priority_level),
      action,
    ];
  });

  const borderGray: RGB = [156, 163, 175]; // #9CA3AF medium gray
  const pageNumberAtTableStart = doc.getCurrentPageInfo().pageNumber;

  autoTable(doc, {
    startY: tableTop,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, bottom: FOOTER_RESERVE, top: TOP_MARGIN_CONT },
    rowPageBreak: "avoid",
    head: [[
      "ID",
      "Finding",
      "Domain / Control",
      "Risk",
      "Priority",
      "Recommended Action",
    ]],
    body,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: { top: 9, right: 7, bottom: 9, left: 7 },
      textColor: REPORT_THEME.textPrimary.rgb as any,
      lineColor: borderGray as any,
      lineWidth: 0.7,
      valign: "top",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: REPORT_THEME.surfaceAlt.rgb as any,
      textColor: REPORT_THEME.navy.rgb as any,
      fontStyle: "bold",
      fontSize: 9,
      halign: "left",
      cellPadding: { top: 9, right: 7, bottom: 9, left: 7 },
      lineColor: borderGray as any,
      lineWidth: 0.7,
    },
    alternateRowStyles: {
      fillColor: [250, 251, 253] as any,
    },
    columnStyles: {
      // Widened Finding (+20) and Domain/Control (+26) so the NIST code fits
      // on a single line; Recommended Action remains "auto" and grows
      // vertically as needed.
      0: { cellWidth: 42, halign: "center", fontStyle: "bold", valign: "middle", fontSize: 8.5, textColor: REPORT_THEME.navy.rgb as any },
      1: { cellWidth: 130 },                       // Finding (wider)
      2: { cellWidth: 96, fontSize: 8.5 },         // Domain / Control (wider)
      3: { cellWidth: 48, halign: "center", valign: "middle" },  // Risk badge
      4: { cellWidth: 56, halign: "center", valign: "middle" },  // Priority badge
      5: { cellWidth: "auto", fontSize: 8.5 },     // Recommended Action — no truncation
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 1) {
        const raw = data.cell.raw as any;
        if (raw && typeof raw === "object" && "title" in raw) {
          data.cell.text = [raw.title];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fontSize = 9;
        }
      }
      if (data.section === "body" && data.column.index === 2) {
        const raw = data.cell.raw as any;
        if (raw && typeof raw === "object" && "csv" in raw) {
          data.cell.text = [];
        }
      }
    },
    didDrawPage: (data) => {
      // Draw chrome on every continuation page (the first page already has
      // header + section title drawn above this autoTable call).
      if (data.pageNumber > 1) {
        drawRunningHeader(doc, {
          clientName: ctx.clientOrgName,
          frameworks: ctx.frameworks ?? null,
          methodology: ctx.methodology ?? null,
        });
        drawSectionTitle(doc, {
          sectionNumber: "04",
          title: "Top Findings & Recommendations",
          continued: true,
        });
      }
    },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      if (data.column.index === 2) {
        const raw = data.cell.raw as any;
        if (!raw || typeof raw !== "object" || !("csv" in raw)) return;
        const csv = String(raw.csv ?? "—");
        const nist = String(raw.nist ?? "").trim();
        const cx = data.cell.x + 7;
        const baseY = data.cell.y + 14;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...REPORT_THEME.navy.rgb);
        const csvLines = doc.splitTextToSize(csv, data.cell.width - 14) as string[];
        doc.text(csvLines, cx, baseY, { lineHeightFactor: 1.2 } as any);
        if (nist) {
          const nistY = baseY + csvLines.length * 10 + 3;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.8);
          doc.setTextColor(...REPORT_THEME.textMuted.rgb);
          const nistLines = doc.splitTextToSize(`NIST: ${nist}`, data.cell.width - 14) as string[];
          doc.text(nistLines, cx, nistY, { lineHeightFactor: 1.2 } as any);
        }
        return;
      }
      if (data.column.index === 3) {
        const value = String(data.cell.raw ?? "—");
        if (value === "—") return;
        const palette = RISK_BADGE[value];
        if (!palette) return;
        paintBadge(doc, data.cell, value, palette.fill, palette.text);
      }
      if (data.column.index === 4) {
        const value = String(data.cell.raw ?? "—");
        if (value === "—") return;
        const palette = PRIORITY_BADGE[value];
        if (!palette) return;
        paintBadge(doc, data.cell, value, palette.fill, palette.text);
      }
    },
  });

  // Empty-state copy when there are zero findings.
  if (top.length === 0) {
    const cursorY = ((doc as any).lastAutoTable?.finalY ?? tableTop) + 18;
    doc.setTextColor(...REPORT_THEME.textMuted.rgb);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.text(
      "No findings have been recorded for this assessment yet.",
      PAGE_MARGIN,
      cursorY,
    );
  }

  // Draw footers across all pages spanned by this section.
  const endPage = doc.getCurrentPageInfo().pageNumber;
  const startPageNumber = ctx.pageNumber ?? 6;
  for (let p = pageNumberAtTableStart, n = 0; p <= endPage; p++, n++) {
    doc.setPage(p);
    drawPageFooter(doc, startPageNumber + n);
  }
  doc.setPage(endPage);
}


function paintBadge(
  doc: jsPDF,
  cell: any,
  label: string,
  fill: RGB,
  text: RGB,
): void {
  const padX = 6;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  const tw = doc.getTextWidth(label);
  const bw = tw + padX * 2;
  const bh = 15;
  const cx = cell.x + cell.width / 2;
  const cy = cell.y + cell.height / 2;
  const bx = cx - bw / 2;
  const by = cy - bh / 2;

  // Wipe the underlying cell text so the badge reads cleanly. Inset by 1pt
  // on all sides to avoid touching the cell border (line width up to 0.7).
  doc.setFillColor(
    cell.styles.fillColor?.[0] ?? 255,
    cell.styles.fillColor?.[1] ?? 255,
    cell.styles.fillColor?.[2] ?? 255,
  );
  doc.rect(cell.x + 1, cell.y + 1, cell.width - 2, cell.height - 2, "F");

  // Rectangular badge (2pt corner radius) — tighter fit inside table cells.
  doc.setFillColor(...fill);
  doc.roundedRect(bx, by, bw, bh, 2, 2, "F");
  doc.setTextColor(...text);
  doc.text(label, cx, by + bh / 2 + 2.8, { align: "center" });

  // Restore cell borders that the wipe may have clipped on adjacent rows.
  const lc = (cell.styles.lineColor as RGB) ?? [156, 163, 175];
  const lw = (cell.styles.lineWidth as number) ?? 0.7;
  doc.setDrawColor(lc[0], lc[1], lc[2]);
  doc.setLineWidth(lw);
  doc.line(cell.x, cell.y, cell.x + cell.width, cell.y);
  doc.line(cell.x, cell.y + cell.height, cell.x + cell.width, cell.y + cell.height);
  doc.line(cell.x, cell.y, cell.x, cell.y + cell.height);
  doc.line(cell.x + cell.width, cell.y, cell.x + cell.width, cell.y + cell.height);
}
